import {
  ImageScaleView,
  ImageScaleViewOpts,
  View,
  ViewOpts,
} from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { FontName } from 'src/lib/ui/config';

type FeatureButtonLabelOpts = {
  localeText?: () => string;
  font?: FontName;
  fontSize?: number;
  labelColor?: string;
  labelOffsetX?: number;
  labelOffsetY?: number;
};

type FeatureButtonBackgroundOpts = Pick<
  ImageScaleViewOpts,
  'image' | 'scaleMethod' | 'sourceSlices' | 'visible'
>;

export type FeatureButtonLabelPanelOpts = {
  width?: number;
  height?: number;
  offsetX?: number;
  offsetY?: number;
  background?: FeatureButtonBackgroundOpts;
} & FeatureButtonLabelOpts;

export class FeatureButtonLabelPanel extends View {
  private bg?: ImageScaleView;
  public readonly label: LangBitmapFontTextView;

  constructor(opts: FeatureButtonLabelPanelOpts & { superview: View }) {
    const {
      superview,
      width = 138,
      height = 45,
      offsetX = 0,
      offsetY = 0,
      localeText,
      font = 'Body',
      fontSize = 20,
      labelColor = '#FFFFFF',
      labelOffsetX = 0,
      labelOffsetY = 0,
      background,
    } = opts;

    super({
      superview,
      width,
      height,
      centerOnOrigin: true,
      x: superview.style.width * 0.5 + offsetX,
      y: superview.style.height - 20 + offsetY,
      zIndex: 1,
    });

    if (background) {
      this.updateBackground(background);
    }

    this.label = new LangBitmapFontTextView({
      superview: this,
      width,
      height,
      align: 'center',
      verticalAlign: 'center',
      size: fontSize,
      font,
      color: labelColor,
      zIndex: 1,
      offsetX: labelOffsetX,
      offsetY: labelOffsetY,
      localeText,
    });
  }

  public override updateOpts(opts: ViewOpts) {
    const superview = this.getSuperview();
    const { offsetX, offsetY } = opts;
    const viewOpts = super.updateOpts({
      ...opts,
      x:
        superview && offsetX !== undefined
          ? superview.style.width * 0.5 + offsetX
          : this.style.x,
      y:
        superview && offsetY !== undefined
          ? superview.style.height - 20 + offsetY
          : this.style.y,
    });
    const { width, height } = this.style;
    this.label.updateOpts({ width, height });
    this.bg?.updateOpts({ width, height });
    return viewOpts;
  }

  public updatePanelOpts(opts: FeatureButtonLabelPanelOpts) {
    const { background } = opts;
    this.updateOpts(opts);
    this.updateLabel(opts);
    if (background) this.updateBackground(background);
  }

  private updateLabel(opts: FeatureButtonLabelOpts) {
    const {
      localeText,
      font,
      fontSize,
      labelColor,
      labelOffsetX,
      labelOffsetY,
    } = opts;
    this.label.updateOpts({
      font,
      size: fontSize,
      color: labelColor,
      offsetX:
        labelOffsetX !== undefined ? labelOffsetX : this.label.style.offsetX,
      offsetY:
        labelOffsetY !== undefined ? labelOffsetY : this.label.style.offsetY,
      localeText,
    });
  }

  private updateBackground(opts: FeatureButtonBackgroundOpts) {
    if (!this.bg) {
      this.bg = new ImageScaleView({
        superview: this,
        width: this.style.width,
        height: this.style.height,
        ...opts,
      });
      return;
    }

    this.bg.updateOpts(opts);
  }
}
